import {Contract} from "shared-types";
import {create} from "zustand";
import {contractApi} from '../../services/api';

interface ContractStore {
    contracts: Contract[];
    contractsByCustomer: Contract[];
    selectedContract: Contract | null;
    loading: boolean;
    error?: string;
    showGraphForId: string | null;

    fetchContracts: () => Promise<void>;
    fetchContractDetails: (contractId: string) => Promise<Contract | null>;
    fetchContractsByCustomerId: (customerId: string) => Promise<void>;
    fetchContractsEndingSoon: (days: number) => Promise<void>;

    handleCreateContract: (contract: Partial<Contract>) => Promise<void>;
    handleUpdateContract: (contractId: string, contract: Partial<Contract>) => Promise<void>;
    handleDeleteContract: (contractId: string) => Promise<void>;
    handleUploadDocuments: (formData: FormData) => Promise<void>;

    handleSelectContract: (contractId: string | null) => void;
    resetSelectedContract: () => void;
    setShowGraphForId: (id: string | null) => void;
}

export const useContractStore = create<ContractStore>((set, get) => ({
    contracts: [],
    contractsByCustomer: [],
    selectedContract: null,
    loading: false,
    error: undefined,
    showGraphForId: null,

    fetchContracts: async () => {
        set({loading: true, error: undefined});
        try {
            const data = await contractApi.getAll();
            set({contracts: data});
        } catch (error: any) {
            set({error: error.message || "שגיאה בטעינת החוזים"});
        } finally {
            set({loading: false});
        }
    },

    fetchContractDetails: async (contractId: string) => {
        set({loading: true, error: undefined});
        try {
            const data = await contractApi.getById(contractId);
            set({selectedContract: data});
            return data;
        } catch (error: any) {
            set({error: error.message || "שגיאה בטעינת חוזה"});
            return null;
        } finally {
            set({loading: false});
        }
    },

    fetchContractsByCustomerId: async (customerId: string) => {
        set({loading: true, error: undefined});
        try {
            const data = await contractApi.getByCustomerId(customerId);
            set({contractsByCustomer: data});
        } catch (error: any) {
            set({error: error.message || "שגיאה בטעינת חוזים לפי לקוח"});
        } finally {
            set({loading: false});
        }
    },

    fetchContractsEndingSoon: async (days: number) => {
        set({loading: true, error: undefined});
        try {
            const data = await contractApi.getEndingSoon(days);
            set({contracts: data});
        } catch (error: any) {
            set({error: error.message || "שגיאה בטעינת חוזים שעומדים להסתיים"});
        } finally {
            set({loading: false});
        }
    },

    handleCreateContract: async (contract: Partial<Contract>) => {
        set({loading: true, error: undefined});
        try {
            await contractApi.create(contract);
            await get().fetchContracts();
        } catch (error: any) {
            set({error: error.message || "שגיאה ביצירת חוזה"});
        } finally {
            set({loading: false});
        }
    },

    handleUpdateContract: async (contractId: string, contract: Partial<Contract>) => {
        set({loading: true, error: undefined});
        try {
            await contractApi.update(contractId, contract);
            await get().fetchContracts();
        } catch (error: any) {
            set({error: error.message || "שגיאה בעדכון חוזה"});
        } finally {
            set({loading: false});
        }
    },

    handleDeleteContract: async (contractId: string) => {
        set({loading: true, error: undefined});
        try {
            await contractApi.delete(contractId);
            await get().fetchContracts();
        } catch (error: any) {
            set({error: error.message || "שגיאה במחיקת חוזה"});
        } finally {
            set({loading: false});
        }
    },

    handleUploadDocuments: async (formData: FormData) => {
        set({loading: true, error: undefined});
        try {
            await contractApi.uploadDocuments(formData);
        } catch (error: any) {
            set({error: error.message || "שגיאה בהעלאת מסמכים לחוזה"});
        } finally {
            set({loading: false});
        }
    },

    handleSelectContract: (contractId: string | null) => {
        if (contractId === null) {
            set({selectedContract: null});
            return;
        }
        const contract = get().contracts.find(c => c.id === contractId) || null;
        set({selectedContract: contract});
    },

    resetSelectedContract: () => {
        set({selectedContract: null});
    },

    setShowGraphForId: (id: string | null) => {
        set({showGraphForId: id});
    },
}));
